import { CombinedGraphQLErrors } from '@apollo/client/errors'

import { isPublicPublishPath } from '~publish/helpers/publicPublishPaths'

export const ALLOW_LOGGED_OUT_AUTH_REJECTION_CONTEXT_KEY =
  'allowLoggedOutAuthRejection'

type GraphQLErrorLike = {
  extensions?: {
    code?: unknown
  } | null
}

const isGraphQLErrorLike = (error: unknown): error is GraphQLErrorLike => {
  if (!error || typeof error !== 'object') {
    return false
  }

  if (!('extensions' in error)) {
    return true
  }

  return error.extensions === null || typeof error.extensions === 'object'
}

const isGraphQLErrorLikeArray = (
  errors: unknown,
): errors is readonly GraphQLErrorLike[] => {
  return Array.isArray(errors) && errors.every(isGraphQLErrorLike)
}

/**
 * True when a network error is an HTTP 401. Auth rejections are handled by
 * auth-specific flows and should not go through generic error handling.
 */
export const isUnauthorizedNetworkError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object' || !('statusCode' in error)) {
    return false
  }

  return error.statusCode === 401
}

export const hasUnauthenticatedGraphQLError = (
  graphQLErrors: readonly GraphQLErrorLike[] | undefined,
): boolean => {
  return (
    graphQLErrors?.some(
      (error) => error.extensions?.code === 'UNAUTHENTICATED',
    ) ?? false
  )
}

export const isAuthRejectionError = (error: unknown): boolean => {
  if (!error) {
    return false
  }

  if (isUnauthorizedNetworkError(error)) {
    return true
  }

  if (CombinedGraphQLErrors.is(error)) {
    return hasUnauthenticatedGraphQLError(error.errors)
  }

  if (typeof error !== 'object') {
    return false
  }

  if (
    'networkError' in error &&
    isUnauthorizedNetworkError(error.networkError)
  ) {
    return true
  }

  if (
    'graphQLErrors' in error &&
    isGraphQLErrorLikeArray(error.graphQLErrors)
  ) {
    return hasUnauthenticatedGraphQLError(error.graphQLErrors)
  }

  return false
}

/**
 * True when the current page is a `/p/*` public Publish page. Only there may
 * auth rejections resolve to logged-out fallbacks; everywhere else the legacy
 * redirect-to-login behavior applies. Derived from the URL at rejection time
 * so it needs no synchronization with React state.
 */
export const isOnPublicPublishPage = (): boolean => {
  return isPublicPublishPath(window.location.pathname)
}
